import {
  AxiosResponse,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosInstance,
  AxiosError,
} from 'axios';
import { captureException } from '@sentry/nextjs';

export type CSRFClient = {
  getToken: () => string | undefined;
  retryWithToken: (
    api: AxiosInstance,
    reqInit: AxiosRequestConfig,
  ) => Promise<AxiosResponse>;
  exponentialRetryWithToken: (
    api: AxiosInstance,
    reqInit: AxiosRequestConfig,
    retriesLeft: number,
    interval: number,
  ) => Promise<AxiosResponse>;
  exponentialRetryWithTokenUpdate: (
    api: AxiosInstance,
    reqInit: AxiosRequestConfig,
    retriesLeft: number,
    interval: number,
  ) => Promise<AxiosResponse>;
};

export const CSRF_TOKEN_HEADER = 'x-csrf-token';
export const CSRF_VALID_HEADER = 'x-csrf-valid';

type HeaderList = Array<Array<string>>;

export const concatHeaders = (
  originalHeaders?: HeadersInit,
  headersToAdd: HeaderList = [],
): AxiosRequestHeaders => {
  if (Array.isArray(originalHeaders)) {
    return Object.fromEntries([...originalHeaders, ...headersToAdd]);
  } else if (originalHeaders instanceof Headers) {
    return {
      ...Object.fromEntries(originalHeaders.entries()),
      ...Object.fromEntries(headersToAdd),
    };
  }
  return {
    ...originalHeaders,
    ...Object.fromEntries(headersToAdd),
  };
};

export const createCSRFClient = (initialToken?: string): CSRFClient => {
  let storedToken: string | undefined = initialToken;

  const csrfProtectedFetchWithRetry = (
    api: AxiosInstance,
    reqInit: AxiosRequestConfig,
    isRetry: boolean = false,
  ): Promise<AxiosResponse> => {
    const headers: HeaderList = [];
    if (!storedToken) {
      captureException(Error(`Missing CSRF Token, requestUri: ${reqInit.url}`));
      throw new Error('Missing CSRF token.');
    }
    headers.push([CSRF_TOKEN_HEADER, storedToken]);
    reqInit.headers = concatHeaders(reqInit.headers, headers);

    return new Promise<AxiosResponse>((resolve, reject) =>
      api(reqInit)
        .then(res => {
          const responseToken = res.headers[CSRF_TOKEN_HEADER];
          storedToken = responseToken || storedToken;
          resolve(res);
        })
        .catch((err: AxiosError) => {
          const responseToken = err.response?.headers[CSRF_TOKEN_HEADER];
          storedToken = responseToken || storedToken;

          if (err.response?.headers[CSRF_VALID_HEADER] === 'false') {
            if (responseToken && !isRetry) {
              resolve(csrfProtectedFetchWithRetry(api, reqInit, true));
            }
          }

          reject(err);
        }),
    );
  };

  const exponentialRetryWithTokenUpdate = async (
    api: AxiosInstance,
    reqInit: AxiosRequestConfig,
    retriesLeft = 5,
    interval = 1000,
  ): Promise<AxiosResponse> => {
    return new Promise<AxiosResponse>((resolve, reject) => {
      return api(reqInit)
        .then(res => {
          const responseToken = res.headers[CSRF_TOKEN_HEADER];
          storedToken = responseToken || storedToken;

          resolve(res);
        })
        .catch((err: AxiosError) => {
          const responseToken = err.response?.headers[CSRF_TOKEN_HEADER];
          storedToken = responseToken || storedToken;

          if (retriesLeft === 1) {
            reject(err);
          } else {
            setTimeout(
              () =>
                exponentialRetryWithTokenUpdate(
                  api,
                  reqInit,
                  retriesLeft - 1,
                  interval * 2,
                ).then(resolve, reject),
              interval,
            );
          }
        });
    });
  };

  const csrfProtectedFetchWithExponentialRetry = async (
    api: AxiosInstance,
    reqInit: AxiosRequestConfig,
    retriesLeft = 5,
    interval = 1000,
  ): Promise<AxiosResponse> => {
    const headers: HeaderList = [];
    if (!storedToken) {
      captureException(Error(`Missing CSRF Token, requestUri: ${reqInit.url}`));
      throw new Error('Missing CSRF token.');
    }
    headers.push([CSRF_TOKEN_HEADER, storedToken]);
    reqInit.headers = concatHeaders(reqInit.headers, headers);

    return new Promise<AxiosResponse>((resolve, reject) => {
      return api(reqInit)
        .then(res => {
          const responseToken = res.headers[CSRF_TOKEN_HEADER];
          storedToken = responseToken || storedToken;

          resolve(res);
        })
        .catch((err: AxiosError) => {
          const responseToken = err.response?.headers[CSRF_TOKEN_HEADER];
          storedToken = responseToken || storedToken;

          if (
            retriesLeft === 1 ||
            (!responseToken &&
              err.response?.headers[CSRF_VALID_HEADER] === 'false')
          ) {
            reject(err);
          } else {
            setTimeout(
              () =>
                csrfProtectedFetchWithExponentialRetry(
                  api,
                  reqInit,
                  retriesLeft - 1,
                  interval * 2,
                ).then(resolve, reject),
              interval,
            );
          }
        });
    });
  };

  return {
    getToken: () => storedToken,
    retryWithToken: (
      api: AxiosInstance,
      reqInit: AxiosRequestConfig,
    ): Promise<AxiosResponse> => {
      return csrfProtectedFetchWithRetry(api, reqInit);
    },
    exponentialRetryWithToken: (
      api: AxiosInstance,
      reqInit: AxiosRequestConfig,
      retriesLeft: number,
      interval: number,
    ): Promise<AxiosResponse> => {
      return csrfProtectedFetchWithExponentialRetry(
        api,
        reqInit,
        retriesLeft,
        interval,
      );
    },
    exponentialRetryWithTokenUpdate: (
      api: AxiosInstance,
      reqInit: AxiosRequestConfig,
      retriesLeft: number,
      interval: number,
    ): Promise<AxiosResponse> => {
      return exponentialRetryWithTokenUpdate(
        api,
        reqInit,
        retriesLeft,
        interval,
      );
    },
  };
};
